import { supabase } from "./supabase";
import { createShoppingListWithServer } from './api';

const OPENAI_API_KEY =
  "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";

export async function generateShoppingList(mealPlan: any, mealPlanId?: string) {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");

    // Extract all ingredients from the meal plan
    const allIngredients = mealPlan.days.flatMap((day: any) =>
      Object.values(day.meals).flatMap((meal: any) => meal.recipe.ingredients),
    );

    // Use OpenAI to consolidate and categorize ingredients in Hebrew
    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${OPENAI_API_KEY}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "gpt-4",
        messages: [
          {
            role: "system",
            content:
              "You are a shopping list organizer that consolidates ingredients and categorizes them in Hebrew. Adjust quantities to match supermarket packaging (e.g., round up milk to 1L or 2L, eggs to 6/12/30, rice to 1kg packages, etc.). For produce, use standard units (e.g., packages, bags, or individual units). Return ONLY valid JSON without any additional text.",
          },
          {
            role: "user",
            content: `Consolidate these ingredients into a shopping list in Hebrew, combining similar items and adjusting quantities to match standard supermarket packaging and units. For example:
- Round milk to 1L/2L containers
- Eggs to 6/12/30 packs
- Rice/flour/sugar to 1kg/2kg packages
- Produce to standard units (bags, packages, or individual units)
- Spices to standard jar sizes
- Canned goods to standard can sizes

Return in this EXACT format (no other text):
{
  "categories": {
    "ירקות": [{"item": "עגבניות שרי", "amount": "1", "unit": "אריזה של 250 גרם"}],
    "מוצרי יסוד": [{"item": "קמח", "amount": "1", "unit": "שקית של 1 קג"}],
    "מוצרי חלב": [{"item": "חלב", "amount": "2", "unit": "ליטר"}],
    "בשר ודגים": [{"item": "חזה עוף", "amount": "500", "unit": "גרם"}],
    "קטניות ודגנים": [{"item": "אורז", "amount": "1", "unit": "קג"}],
    "תבלינים": [{"item": "מלח", "amount": "1", "unit": "חבילה"}],
    "שימורים ורטבים": [{"item": "רסק עגבניות", "amount": "2", "unit": "קופסאות"}],
    "שונות": [{"item": "שמן זית", "amount": "1", "unit": "ליטר"}]
  }
}

Ingredients: ${JSON.stringify(allIngredients)}`,
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    if (!data.choices?.[0]?.message?.content) {
      throw new Error("Invalid API response format");
    }

    try {
      // Parse the OpenAI response
      const shoppingList = JSON.parse(data.choices[0].message.content);
      let serverResponse = null;

      // Save to database if we have a meal plan ID
      if (mealPlanId) {
        let shoppingListId: string;
        
        // First, check if a shopping list already exists
        const { data: existingList } = await supabase
          .from("shopping_lists")
          .select("id")
          .eq("meal_plan_id", mealPlanId)
          .single();

        if (existingList) {
          // Update existing list
          const { error } = await supabase
            .from("shopping_lists")
            .update({ list_data: shoppingList })
            .eq("id", existingList.id);

          if (error) throw error;
          shoppingListId = existingList.id;
          console.log('Updated shopping list with ID:', shoppingListId);
        } else {
          // Create new list
          const { data: newList, error } = await supabase
            .from("shopping_lists")
            .insert({
              user_id: user.id,
              meal_plan_id: mealPlanId,
              list_data: shoppingList,  // Save the OpenAI response here
            })
            .select()
            .single();

          if (error) throw error;
          if (!newList) throw new Error("Failed to create shopping list");
          shoppingListId = newList.id;
          console.log('Created new shopping list with ID:', shoppingListId);
        }

        // After saving to Supabase, send to local server
        try {
          serverResponse = await createShoppingListWithServer(shoppingListId);
          console.log('Server Processing Response:', {
            status: serverResponse.status,
            matchCount: serverResponse.matches?.length,
            shoppingListId: shoppingListId
          });
        } catch (serverError) {
          console.error("Failed to process shopping list on server:", serverError);
        }
      }

      return {
        shoppingList,
        serverProcessing: serverResponse
      };
    } catch (parseError) {
      console.error("Failed to parse shopping list:", parseError);
      throw new Error("Failed to generate shopping list");
    }
  } catch (error) {
    console.error("Error generating shopping list:", error);
    throw error;
  }
}

export async function getShoppingList(mealPlanId: string) {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated");

  const { data, error } = await supabase
    .from("shopping_lists")
    .select("*")
    .eq("meal_plan_id", mealPlanId)
    .single();

  if (error) throw error;
  return data?.list_data;
}
